/**
 * Plan Limits — single client-side source of truth for plan defaults and
 * per-clinic overrides.
 *
 * Plan defaults live in PLAN_LIMITS (re-exported by planService.js for
 * backwards compatibility). A clinic doc may carry:
 *   - `planOverrides` map (NiV-only, validated by firestore.rules):
 *       smsPerMonth, aiActionsPerMonth, maxPatients, maxRooms, maxTherapists,
 *       canCreateRooms, canAddTherapists, canIntegrateMorning, canIntegrateGrow,
 *       canUseLeadsWebhook, leadsInboundPerMonth (leads_plan.md §4.4)
 *   - legacy per-clinic quota fields that predate planOverrides:
 *       videoMeetingMinutesLimit, aiTreatmentsReportMonthlyLimit,
 *       aiTreatmentsReportEnabled, allowedStorageInMbMaxPerClinic
 *
 * `getEffectiveLimits(plan, clinicData)` merges all three layers. With no
 * overrides it is byte-identical to the plan defaults — that invariant is
 * covered by planLimits.test.js and MUST hold (production enforcement).
 *
 * Mirror: functions/planLimitsHelper.js (server). Keep both in sync.
 */

// "unlimited" is represented as 1100 internally (display rule: `< 1100 ? n : "∞"`)
export const UNLIMITED = 1100;
export const isUnlimited = (n) => Number(n) >= UNLIMITED;

const PREMIUM_LIMITS = {
  smsPerMonth: UNLIMITED,
  aiActionsPerMonth: UNLIMITED,
  maxRooms: UNLIMITED,
  maxTherapists: UNLIMITED,
  maxPatients: UNLIMITED,
  canIntegrateMorning: true,
  canIntegrateGrow: true,
  canUseLeadsWebhook: true,
  leadsInboundPerMonth: UNLIMITED,
};

export const PLAN_LIMITS = {
  trial: {
    name: "Trial",
    nameHebrew: "ניסיון",
    smsPerMonth: 20,
    aiActionsPerMonth: 50,
    maxRooms: 0,
    maxTherapists: 1, // Solo clinic only
    maxPatients: 15,
    trialDays: 30,
    maxWriteOpsPerTrial: 3000,
    canIntegrateMorning: true,
    canIntegrateGrow: true,
    canUseLeadsWebhook: true,
    leadsInboundPerMonth: 50,
  },
  starter: {
    name: "Starter",
    nameHebrew: "סטארטר",
    smsPerMonth: 250,
    aiActionsPerMonth: UNLIMITED,
    maxRooms: 0,
    maxTherapists: 1, // Solo clinic only
    maxPatients: 10,
    canIntegrateMorning: true,
    canIntegrateGrow: true,
    canUseLeadsWebhook: true,
    leadsInboundPerMonth: UNLIMITED,
  },
  "starter-plus": {
    name: "Starter Plus",
    nameHebrew: "סטארטר פלוס",
    smsPerMonth: 250,
    aiActionsPerMonth: UNLIMITED,
    maxRooms: 0,
    maxTherapists: 1, // Solo clinic only
    maxPatients: 15,
    canIntegrateMorning: true,
    canIntegrateGrow: true,
    canUseLeadsWebhook: true,
    leadsInboundPerMonth: UNLIMITED,
  },
  premium: {
    name: "Premium",
    nameHebrew: "פרימיום",
    ...PREMIUM_LIMITS,
  },
  // Pilot plan — free program with identical capabilities to Premium.
  // Kept under the "custom1" plan code for backward compatibility.
  custom1: { name: "Pilot - Free", nameHebrew: "פיילוט - חינם", ...PREMIUM_LIMITS },
  // Custom plans (reserved for future custom groups — same as Premium)
  custom2: { name: "Custom 2", nameHebrew: "מותאם 2", ...PREMIUM_LIMITS },
  custom3: { name: "Custom 3", nameHebrew: "מותאם 3", ...PREMIUM_LIMITS },
  custom4: { name: "Custom 4", nameHebrew: "מותאם 4", ...PREMIUM_LIMITS },
  custom5: { name: "Custom 5", nameHebrew: "מותאם 5", ...PREMIUM_LIMITS },
  custom6: { name: "Custom 6", nameHebrew: "מותאם 6", ...PREMIUM_LIMITS },
  custom7: { name: "Custom 7", nameHebrew: "מותאם 7", ...PREMIUM_LIMITS },
  custom8: { name: "Custom 8", nameHebrew: "מותאם 8", ...PREMIUM_LIMITS },
  custom9: { name: "Custom 9", nameHebrew: "מותאם 9", ...PREMIUM_LIMITS },
  custom10: { name: "Custom 10", nameHebrew: "מותאם 10", ...PREMIUM_LIMITS },
};

/** Keys allowed inside clinic.planOverrides (mirrors firestore.rules allowlist) */
export const OVERRIDE_NUMBER_KEYS = [
  "smsPerMonth",
  "aiActionsPerMonth",
  "maxPatients",
  "maxRooms",
  "maxTherapists",
  "leadsInboundPerMonth",
];
export const OVERRIDE_BOOL_KEYS = ["canCreateRooms", "canAddTherapists", "canIntegrateMorning", "canIntegrateGrow", "canUseLeadsWebhook"];
export const OVERRIDE_KEYS = [...OVERRIDE_NUMBER_KEYS, ...OVERRIDE_BOOL_KEYS];

/**
 * Legacy per-clinic quota fields stored flat on the clinic doc.
 * `key` is the name used inside effective limits; `field` the Firestore field.
 * `null`/absent field = plan default.
 */
export const LEGACY_LIMIT_FIELDS = {
  videoMinutesPerMonth: {
    field: "videoMeetingMinutesLimit",
    type: "number",
    getDefault: () => 300,
  },
  aiTreatmentsReportLimit: {
    field: "aiTreatmentsReportMonthlyLimit",
    type: "number",
    getDefault: (plan) => (plan === "trial" ? 5 : 40),
  },
  aiTreatmentsReportEnabled: {
    field: "aiTreatmentsReportEnabled",
    type: "boolean",
    getDefault: () => true,
  },
  storageMb: {
    field: "allowedStorageInMbMaxPerClinic",
    type: "number",
    getDefault: () => 1000,
  },
};

export const normalizePlanCode = (planName) => {
  const normalized = String(planName || "trial").toLowerCase();
  return PLAN_LIMITS[normalized] ? normalized : "trial";
};

export const getPlanLimits = (planName) => PLAN_LIMITS[normalizePlanCode(planName)];

const isValidInt = (v) => typeof v === "number" && Number.isFinite(v) && Number.isInteger(v) && v >= 0;

/**
 * Plan defaults expanded with derived booleans + legacy quota defaults.
 * Shape is the full "restriction sheet" the NiV UI displays.
 */
export const getPlanDefaults = (planName) => {
  const plan = normalizePlanCode(planName);
  const base = PLAN_LIMITS[plan];
  const defaults = {
    plan,
    name: base.name,
    nameHebrew: base.nameHebrew,
    smsPerMonth: base.smsPerMonth,
    aiActionsPerMonth: base.aiActionsPerMonth,
    maxPatients: base.maxPatients,
    maxRooms: base.maxRooms,
    maxTherapists: base.maxTherapists,
    canCreateRooms: base.maxRooms > 0,
    canAddTherapists: base.maxTherapists > 1,
    canIntegrateMorning: base.canIntegrateMorning === true,
    canIntegrateGrow: base.canIntegrateGrow === true,
    canUseLeadsWebhook: base.canUseLeadsWebhook !== false,
    leadsInboundPerMonth: base.leadsInboundPerMonth ?? UNLIMITED,
    trialDays: base.trialDays ?? null,
    maxWriteOpsPerTrial: base.maxWriteOpsPerTrial ?? null,
  };
  Object.entries(LEGACY_LIMIT_FIELDS).forEach(([key, def]) => {
    defaults[key] = def.getDefault(plan);
  });
  return defaults;
};

/**
 * Sanitized copy of clinic.planOverrides — only allowlisted keys with the
 * right primitive type survive. Never throws.
 */
export const sanitizeOverrides = (raw) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out = {};
  OVERRIDE_NUMBER_KEYS.forEach((k) => {
    if (isValidInt(raw[k])) out[k] = raw[k];
  });
  OVERRIDE_BOOL_KEYS.forEach((k) => {
    if (typeof raw[k] === "boolean") out[k] = raw[k];
  });
  return out;
};

/**
 * Effective limits = plan defaults ← planOverrides ← legacy per-clinic fields,
 * plus coupling rules (a disabled capability zeroes its count limit).
 *
 * @param {string} planName clinic.plan
 * @param {object|null} clinicData clinic doc (or any object carrying the same fields)
 */
export const getEffectiveLimits = (planName, clinicData) => {
  const defaults = getPlanDefaults(planName);
  const overrides = sanitizeOverrides(clinicData?.planOverrides);
  const effective = { ...defaults, ...overrides };

  Object.entries(LEGACY_LIMIT_FIELDS).forEach(([key, def]) => {
    const v = clinicData?.[def.field];
    if (def.type === "number") {
      if (typeof v === "number" && Number.isFinite(v) && v > 0) effective[key] = v;
    } else if (typeof v === "boolean") {
      effective[key] = v;
    }
  });

  // Coupling between capability flags and count limits:
  //  - an explicit flag override wins (false → count 0/1, true with a zero
  //    count → unlimited count);
  //  - otherwise the count (default or overridden) decides the flag.
  const roomsFlag =
    overrides.canCreateRooms ?? (overrides.maxRooms != null ? overrides.maxRooms > 0 : defaults.canCreateRooms);
  if (roomsFlag === false) effective.maxRooms = 0;
  else if (effective.maxRooms <= 0) effective.maxRooms = UNLIMITED;
  effective.canCreateRooms = effective.maxRooms > 0;

  const therapistsFlag =
    overrides.canAddTherapists ??
    (overrides.maxTherapists != null ? overrides.maxTherapists > 1 : defaults.canAddTherapists);
  if (therapistsFlag === false) effective.maxTherapists = 1;
  else if (effective.maxTherapists <= 1) effective.maxTherapists = UNLIMITED;
  effective.canAddTherapists = effective.maxTherapists > 1;

  return effective;
};

/** Keys compared when computing "differs from plan" flags (display order). */
export const DIFF_KEYS = [
  "smsPerMonth",
  "aiActionsPerMonth",
  "maxPatients",
  "maxRooms",
  "maxTherapists",
  "canCreateRooms",
  "canAddTherapists",
  "canIntegrateMorning",
  "canIntegrateGrow",
  "canUseLeadsWebhook",
  "leadsInboundPerMonth",
  "storageMb",
  "videoMinutesPerMonth",
  "aiTreatmentsReportEnabled",
  "aiTreatmentsReportLimit",
];

/**
 * List of effective values that differ from the plan default.
 * @returns {Array<{ key, effective, planDefault, source: "planOverrides"|"legacy" }>}
 */
export const diffFromPlan = (planName, clinicData) => {
  const defaults = getPlanDefaults(planName);
  const effective = getEffectiveLimits(planName, clinicData);
  return DIFF_KEYS.filter((k) => effective[k] !== defaults[k]).map((k) => ({
    key: k,
    effective: effective[k],
    planDefault: defaults[k],
    source: LEGACY_LIMIT_FIELDS[k] ? "legacy" : "planOverrides",
  }));
};
